import * as React from 'react';
import i18next from 'i18next';
import { Portal, PortalNamespace } from '../Portal';
import { FocusTrap } from 'focus-trap-react';

import { H1 } from '../Heading';
import { IconCancel } from '../Icon';
import {
  Body,
  Content,
  Dialog,
  Header,
  Heading,
  UiBlock,
  Wrapper,
  Close,
  CloseBtn,
} from './wrapper';
import { useEffect, useId } from 'react';

interface ModalProps {
  id?: string | null;
  title: string | null;
  children: React.ReactNode;
  onClose: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>) => void;
}

const Modal = ({ id, title, onClose, children }: ModalProps) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose({} as React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <Portal id={id || PortalNamespace.DefaultPortal}>
      <FocusTrap focusTrapOptions={{
        clickOutsideDeactivates: true,
        fallbackFocus: () => dialogRef.current as HTMLElement,
      }}>
        <Wrapper ref={dialogRef} aria-labelledby={titleId}>
          <UiBlock onClick={onClose} />
          <Dialog>
            <Content>
              <Header>
                <Heading>
                  <H1 noMargin id={titleId}>{title}</H1>
                </Heading>
                <Close>
                  <CloseBtn onClick={onClose} aria-label={i18next.t('other.closeModal') || ''}>
                    <IconCancel block aria-hidden="true" />
                  </CloseBtn>
                </Close>
              </Header>
              <Body>{children}</Body>
            </Content>
          </Dialog>
        </Wrapper>
      </FocusTrap>
    </Portal>
  );
};

export default Modal;
